import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Stack,
} from '@chakra-ui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { BigNumber } from 'bignumber.js';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { FormErrors } from '@blockpulse3/data/shared';
import { useGetFundraisingQuery } from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency, getNearestMultiple } from '@blockpulse3/helpers';
import { ErrorMessage, ErrorQueryCard } from '@blockpulse3/ui/commons';

import { getPrivateFundraisingParameters } from '../../../utils';
import { schema } from './schema';
import { IFundraisingNaturalSubscriberInformations } from './types';

type Props = {
  defaultValues: IFundraisingNaturalSubscriberInformations;
  onSubmit: (data: IFundraisingNaturalSubscriberInformations) => void;
};

export function NaturalSubscriberInformationsForm({ defaultValues, onSubmit }: Props): JSX.Element {
  const i18nAlertAmount = useTranslations('AlertAmount');
  const i18nNaturalSubscriberInformationsForm = useTranslations(
    'NaturalSubscriberInformationsForm',
  );

  const { fundraisingId = '' } = useParams();

  /* ** Track amount touched state ** */
  const [isAmountTouched, setIsAmountTouched] = useState<boolean>(false);

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
  });

  const fundraising = data?.fundraising;
  const { pricePerShare } = getPrivateFundraisingParameters(fundraising);

  const { register, formState, handleSubmit, watch, setError } =
    useForm<IFundraisingNaturalSubscriberInformations>({
      defaultValues,
      resolver: yupResolver(schema),
    });

  if (loading) {
    //TODO: remove spinner
    return <Spinner />;
  }

  if (error) {
    return <ErrorQueryCard />;
  }

  /* ** Compute amount to a multiple of the pricePerShare ** */
  const amount = new BigNumber(watch('amount'));
  const fixedAmount = getNearestMultiple(amount, pricePerShare);
  const isFixed = !amount.isEqualTo(fixedAmount) && !fixedAmount.isNaN();
  const isNull = fixedAmount.isZero() || fixedAmount.isNaN();
  const showAlert = (isFixed || isNull) && isAmountTouched;

  const handleFormSubmit: SubmitHandler<IFundraisingNaturalSubscriberInformations> = (
    data,
  ): void => {
    /* ** Check for a new condition on submit. Forbid a 0 value rounded amount ** */
    const nbAmount = fixedAmount.toNumber();
    if (nbAmount === 0) {
      setError('amount', { type: 'custom', message: FormErrors.PositiveFormat });
    } else {
      onSubmit({ ...data, amount: nbAmount });
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLFormElement>): void => {
    /* ** Avoid onBlur formState.touchedFields update ** */
    if (e.target.id === 'amount') {
      setIsAmountTouched(true);
    }
  };

  return (
    <form onChange={handleFormChange} onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing="8">
        <Stack spacing="4">
          <FormControl isInvalid={!!formState.errors.email}>
            <FormLabel htmlFor="email">{i18nNaturalSubscriberInformationsForm('Email')}</FormLabel>
            <Input id="email" type="string" {...register('email')} />
            <ErrorMessage error={formState.errors.email} />
          </FormControl>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors.firstName}>
              <FormLabel htmlFor="firstName">
                {i18nNaturalSubscriberInformationsForm('FirstName')}
              </FormLabel>
              <Input id="firstName" type="string" {...register('firstName')} />
              <ErrorMessage error={formState.errors.firstName} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors.lastName}>
              <FormLabel htmlFor="lastName">
                {i18nNaturalSubscriberInformationsForm('LastName')}
              </FormLabel>
              <Input id="lastName" type="string" {...register('lastName')} />
              <ErrorMessage error={formState.errors.lastName} />
            </FormControl>
          </Stack>
          <FormControl isInvalid={!!formState.errors.amount}>
            <FormLabel htmlFor="amount">
              {i18nNaturalSubscriberInformationsForm('Amount')}
            </FormLabel>
            <InputGroup>
              <Input id="amount" step="0.01" type="number" {...register('amount')} />
              <InputRightAddon>€</InputRightAddon>
            </InputGroup>
            <ErrorMessage error={formState.errors.amount} />
            {showAlert && (
              <Alert mt="3" status="warning">
                <AlertIcon as={ExclamationIcon} />
                <Stack spacing="1">
                  <AlertTitle>{i18nAlertAmount('Title')}</AlertTitle>
                  <AlertDescription>
                    {isNull
                      ? i18nAlertAmount('DescriptionZero')
                      : i18nAlertAmount('Description', {
                          fixedAmount: formatNumberCurrency(fixedAmount),
                        })}
                  </AlertDescription>
                </Stack>
              </Alert>
            )}
          </FormControl>
          <FormControl isInvalid={!!formState.errors.admissionFees}>
            <FormLabel htmlFor="admissionFees">
              {i18nNaturalSubscriberInformationsForm('EntranceFee')}
            </FormLabel>
            <InputGroup>
              <Input id="admissionFees" step="0.01" type="number" {...register('admissionFees')} />
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
            <ErrorMessage error={formState.errors.admissionFees} />
          </FormControl>
        </Stack>
        <Button data-cy="validate-subscriber" type="submit" w="full">
          {i18nNaturalSubscriberInformationsForm('AddSubscriber')}
        </Button>
      </Stack>
    </form>
  );
}

export type NaturalSubscriberInformationsFormProps = Props;
